/* GASSISA
=======================================================================================
* Descripción: Modal para generar reporte 80-20 de ventas
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
*      1       | 26/03/2026 | Lic. José Luis Aragón Cervantes  | Creación
* ---------------------------------------------------------------------------------------
*/

import React, { Fragment, useState, useEffect } from "react";
import { Modal, Box, Typography, Button, Divider, Alert } from "@mui/material";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useSesion } from "../../context/System/SesionContext";
import { useLayout } from "../../context/System/LayoutContext";
import { useRequest } from "../../context/System/RequestContext";
import { apiReporte8020, apiReporte8020Agrupado, apiReporte8020MarcaModelo } from "../../settings/ApiConfig";

const modalStyle = {
    position: 'fixed',
    top: '5%',
    left: '30%',
    width: '40vw',
    maxWidth: '90vw',
    maxHeight: '90vh',
    bgcolor: 'background.paper',
    boxShadow: 24,
    p: 3,
    borderRadius: 2,
    overflowY: 'auto',
};

export default function ModalReporte8020(props) {
    const { openModal = false, onClose } = props;
    
    const { PostFileRequest } = useRequest();
    const { usuarioData, parametrosCajaForm } = useSesion();
    const { handleOpenAlert } = useLayout();

    const [internalOpen, setInternalOpen] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [fechaInicio, setFechaInicio] = useState(new Date());
    const [fechaFin, setFechaFin] = useState(new Date());
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (openModal) {
            setInternalOpen(true);
            const hoy = new Date();
            setFechaFin(hoy);
            setTimeout(() => setModalVisible(true), 50);
        } else {
            setModalVisible(false);
            setTimeout(() => setInternalOpen(false), 300);
        }
    }, [openModal]);

    const getFechaLocal = (date) => {
        const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const handleGenerarReporte = async (tipo) => {
        if (fechaFin < fechaInicio) {
            handleOpenAlert("La fecha final no puede ser anterior a la fecha inicial", "warning");
            return;
        }

        setLoading(true);
        try {
            const data = {
                fechainicio: getFechaLocal(fechaInicio),
                fechafin: getFechaLocal(fechaFin),
                sucursal: parametrosCajaForm?.sucursal ?? '',
                usuario: usuarioData?.Usuario ?? '',
                tipousuario: "ADMINISTRADOR",
            };

            const urlMap = {
                normal: { url: apiReporte8020, filename: `Reporte8020-${getFechaLocal(fechaInicio)}.pdf`, msg: "Reporte 80-20 generado exitosamente" },
                marcamodelo: { url: apiReporte8020MarcaModelo, filename: `Reporte8020MarcaModelo-${getFechaLocal(fechaInicio)}.pdf`, msg: "Reporte 80-20 por marca/modelo generado exitosamente" },
                agrupado: { url: apiReporte8020Agrupado, filename: `Reporte8020Agrupado-${getFechaLocal(fechaInicio)}.pdf`, msg: "Reporte 80-20 agrupado generado exitosamente" },
            };

            const cfg = urlMap[tipo] || urlMap.normal;

            const result = await PostFileRequest({
                url: cfg.url,
                body: data,
                filename: cfg.filename,
                openInNewTab: true,
                loader: "Cargando reporte...",
            });

            if (!result) throw new Error("No se pudo generar el reporte");

            handleOpenAlert(cfg.msg, "success");
            onClose();
        } catch (error) {
            handleOpenAlert("Error al generar reporte: " + error.message, "error");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            open={internalOpen}
            onClose={onClose}
            closeAfterTransition
        >
            <Box sx={{ ...modalStyle, transform: modalVisible ? 'scale(1)' : 'scale(0.8)', opacity: modalVisible ? 1 : 0, transition: 'all 0.3s ease-in-out' }}>
                <Fragment>
                    <Typography variant="h6" component="h2" gutterBottom>
                        Reporte + vendidos
                    </Typography>
                    
                    <Divider sx={{ my: 2 }} />

                    <Alert severity="info" sx={{ mb: 2 }}>
                        <Typography variant="body2">
                            <strong>Análisis Pareto:</strong> Clasifica artículos por contribución a ventas. 
                            El 80% de ventas proviene del 20% de productos (Clase A). 
                            Identifica productos clave para enfocarte y productos de baja rotación.
                        </Typography>
                    </Alert>

                    <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle1" gutterBottom>
                            Seleccionar rango de fechas del reporte:
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                            <Box>
                                <Typography variant="caption" display="block" gutterBottom>Fecha Inicio</Typography>
                                <DatePicker
                                    selected={fechaInicio}
                                    onChange={(date) => setFechaInicio(date)}
                                    dateFormat="dd/MM/yyyy"
                                    className="custom-datepicker"
                                />
                            </Box>
                            <Box>
                                <Typography variant="caption" display="block" gutterBottom>Fecha Fin</Typography>
                                <DatePicker
                                    selected={fechaFin}
                                    onChange={(date) => setFechaFin(date)}
                                    dateFormat="dd/MM/yyyy"
                                    className="custom-datepicker"
                                    minDate={fechaInicio}
                                />
                            </Box>
                        </Box>
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                        <Button 
                            variant="outlined" 
                            onClick={onClose}
                            disabled={loading}
                        >
                            Cancelar
                        </Button>
                        <Button 
                            variant="contained" 
                            color="warning"
                            onClick={() => handleGenerarReporte("marcamodelo")}
                            disabled={loading}
                            sx={{ fontSize: '0.75rem' }}
                        >
                            {loading ? "Generando..." : "Marca/Modelo"}
                        </Button>
                        <Button 
                            variant="contained" 
                            color="success"
                            onClick={() => handleGenerarReporte("agrupado")}
                            disabled={loading}
                        >
                            {loading ? "Generando..." : "Gpo. Artículo PDF"}
                        </Button>
                        <Button 
                            variant="contained" 
                            color="primary"
                            onClick={() => handleGenerarReporte("normal")}
                            disabled={loading}
                        >
                            {loading ? "Generando..." : "Generar Reporte PDF"}
                        </Button>
                    </Box>
                </Fragment>
            </Box>
        </Modal>
    );
}
